import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Building2,
  Search,
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  MessageSquare,
  FileText,
  Mail,
  Key,
  User,
  Users,
  Calendar,
  MapPin,
  Edit2,
  Trash2,
  DollarSign,
  Link2,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

// Estados del onboarding con flujo de doble aprobación
const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-slate-200 text-slate-900 font-medium', icon: Clock },
  EN_PROGRESO: { label: 'En Progreso', color: 'bg-blue-200 text-blue-900 font-medium', icon: RefreshCw },
  ENVIADO: { label: 'Enviado', color: 'bg-purple-200 text-purple-900 font-medium', icon: Send },
  EN_REVISION: { label: 'En Revisión', color: 'bg-amber-200 text-amber-900 font-medium', icon: Eye },
  INFO_ADICIONAL: { label: 'Info Adicional', color: 'bg-orange-200 text-orange-900 font-medium', icon: AlertTriangle },
  NIVEL_1_APROBADO: { label: 'Aprobado Nivel 1', color: 'bg-cyan-200 text-cyan-900 font-medium', icon: CheckCircle },
  NIVEL_1_DEVUELTO: { label: 'Devuelto por Nivel 2', color: 'bg-amber-200 text-amber-900 font-medium', icon: AlertTriangle },
  APROBADO: { label: 'Aprobado', color: 'bg-green-200 text-green-900 font-medium', icon: CheckCircle },
  RECHAZADO: { label: 'Rechazado', color: 'bg-red-200 text-red-900 font-medium', icon: XCircle },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function SolicitudesOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [filterEstado, setFilterEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail modal
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Decision modal
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decision, setDecision] = useState('');
  const [decisionComment, setDecisionComment] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [limiteDiario, setLimiteDiario] = useState('');
  const [limiteMensual, setLimiteMensual] = useState('');
  
  // Comment
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  
  // Edit modal
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Document decision
  const [showDocDecisionDialog, setShowDocDecisionDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [docDecision, setDocDecision] = useState('');
  const [docDecisionComment, setDocDecisionComment] = useState('');
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendHours, setExtendHours] = useState(168);
  const [extendNoExpiration, setExtendNoExpiration] = useState(false);
  const [extending, setExtending] = useState(false);

  // Ver / Cambiar credenciales de acceso del cliente
  const [showCredsDialog, setShowCredsDialog] = useState(false);
  const [credsInfo, setCredsInfo] = useState(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [credsCustomPassword, setCredsCustomPassword] = useState('');
  const [credsSendEmail, setCredsSendEmail] = useState(false);
  const [credsSetting, setCredsSetting] = useState(false);
  const [credsResult, setCredsResult] = useState(null);
  
  // Modificar límites
  const [showModificarLimites, setShowModificarLimites] = useState(false);
  const [nuevosLimites, setNuevosLimites] = useState({ diario: '', mensual: '' });
  const [limitesComentario, setLimitesComentario] = useState('');
  const [modificandoLimites, setModificandoLimites] = useState(false);

  useEffect(() => {
    loadRequests();
    loadCounts();
  }, [filterEstado]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      
      const res = await api.get(`/onboarding/requests?${params.toString()}`);
      setRequests(res.data || []);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const res = await api.get('/onboarding/requests/count');
      setCounts(res.data || {});
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const loadRequestDetail = async (requestId) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/onboarding/requests/${requestId}`);
      setSelectedRequest(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDecision = async () => {
    // Validar decisión
    if (!decision) {
      toast.error('Seleccione una decisión');
      return;
    }
    
    const estado = selectedRequest?.estado;
    const esNivel2 = estado === 'NIVEL_1_APROBADO';
    const esDevuelto = estado === 'NIVEL_1_DEVUELTO';
    
    // VALIDACIÓN CRÍTICA: Evitar enviar APROBADO cuando el estado es ENVIADO/EN_REVISION
    if (['ENVIADO', 'EN_REVISION'].includes(estado) && decision === 'APROBADO') {
      console.error('Bug detectado: Intentando enviar APROBADO cuando estado es', estado);
      toast.error('Error: Esta solicitud requiere aprobación de Nivel 1 primero. Recargue la página.');
      return;
    }
    
    // Validar comentario según el tipo de decisión
    if (['RECHAZADO', 'INFO_ADICIONAL', 'NIVEL_1_DEVUELTO'].includes(decision) && !decisionComment.trim()) {
      toast.error('Debe ingresar un comentario/motivo');
      return;
    }
    
    // Validar límites para aprobaciones (nivel 1 o nivel 2)
    if (decision === 'NIVEL_1_APROBADO' || (decision === 'APROBADO' && esNivel2)) {
      // En nivel 2, si no ingresa límites, usa los propuestos
      const usarLimitesPropuestos = esNivel2 && !limiteDiario && !limiteMensual;
      
      if (!usarLimitesPropuestos) {
        if (!limiteDiario || !limiteMensual) {
          toast.error('Debe ingresar los límites diario y mensual');
          return;
        }
        if (parseFloat(limiteDiario) <= 0 || parseFloat(limiteMensual) <= 0) {
          toast.error('Los límites deben ser mayores a 0');
          return;
        }
        if (parseFloat(limiteDiario) > parseFloat(limiteMensual)) {
          toast.error('El límite diario no puede ser mayor al mensual');
          return;
        }
      }
    }
    
    setDeciding(true);
    try {
      const payload = {
        decision,
        comentario: decisionComment || (decision.includes('APROBADO') ? 'Aprobado' : '')
      };
      
      // Agregar límites si corresponde
      if (decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') {
        if (limiteDiario && limiteMensual) {
          payload.limites = {
            diario: parseFloat(limiteDiario),
            mensual: parseFloat(limiteMensual)
          };
        }
      }
      
      await api.post(`/onboarding/requests/${selectedRequest.id}/decide`, payload);
      
      const mensajes = {
        'NIVEL_1_APROBADO': 'Solicitud aprobada - Enviada a segundo nivel',
        'APROBADO': 'Solicitud aprobada definitivamente - Empresa creada',
        'RECHAZADO': 'Solicitud rechazada',
        'INFO_ADICIONAL': 'Se solicitó información adicional',
        'NIVEL_1_DEVUELTO': 'Solicitud devuelta al primer revisor'
      };
      
      toast.success(mensajes[decision] || 'Decisión procesada');
      setShowDecisionDialog(false);
      setShowDetailDialog(false);
      setLimiteDiario('');
      setLimiteMensual('');
      setDecisionComment('');
      setDecision('');
      loadRequests();
      loadCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar decisión');
    } finally {
      setDeciding(false);
    }
  };

  const handleResendCredentials = async (requestId) => {
    try {
      await api.post(`/onboarding/requests/${requestId}/resend-credentials`);
      toast.success('Credenciales reenviadas');
    } catch (error) {
      toast.error('Error al reenviar credenciales');
    }
  };

  const openCredsDialog = async () => {
    if (!selectedRequest) return;
    setCredsResult(null);
    setCredsCustomPassword('');
    setCredsSendEmail(false);
    setCredsInfo(null);
    setShowCredsDialog(true);
    setCredsLoading(true);
    try {
      const res = await api.get(`/onboarding/requests/${selectedRequest.id}/credentials`);
      setCredsInfo(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al obtener credenciales');
    } finally {
      setCredsLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!selectedRequest) return;
    setCredsSetting(true);
    try {
      const res = await api.post(`/onboarding/requests/${selectedRequest.id}/set-password`, {
        password: credsCustomPassword.trim() || null,
        send_email: credsSendEmail,
      });
      setCredsResult(res.data);
      if (res.data.email_sent) {
        toast.success('Contraseña actualizada y reenviada por email');
      } else if (credsSendEmail && res.data.email_error) {
        toast.warning('Contraseña actualizada, pero falló el envío de email');
      } else {
        toast.success('Contraseña temporal actualizada');
      }
      setCredsInfo((prev) => prev ? { ...prev, password_expires_at: res.data.password_expires_at, has_expiration: res.data.has_expiration, is_expired: false } : prev);
      await loadRequestDetail(selectedRequest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al setear la contraseña');
    } finally {
      setCredsSetting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado`),
      () => toast.error('No se pudo copiar')
    );
  };


  const handleExtendPassword = async () => {
    if (!selectedRequest) return;
    setExtending(true);
    try {
      const res = await api.post(`/onboarding/requests/${selectedRequest.id}/extend-password`, {
        expiration_hours: extendNoExpiration ? null : extendHours,
        no_expiration: extendNoExpiration,
      });
      toast.success(res.data.message || 'Vencimiento actualizado');
      setShowExtendDialog(false);
      await loadRequestDetail(selectedRequest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el vencimiento');
    } finally {
      setExtending(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await api.post(`/onboarding/requests/${selectedRequest.id}/comment`, {
        message: newComment,
        is_internal: isInternalComment
      });
      toast.success('Comentario agregado');
      setNewComment('');
      loadRequestDetail(selectedRequest.id);
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleModificarLimites = async (accion) => {
    if (!nuevosLimites.diario || !nuevosLimites.mensual) {
      toast.error('Debe ingresar ambos límites');
      return;
    }
    
    const diario = parseFloat(nuevosLimites.diario);
    const mensual = parseFloat(nuevosLimites.mensual);
    
    if (diario <= 0 || mensual <= 0) {
      toast.error('Los límites deben ser mayores a 0');
      return;
    }
    
    if (diario > mensual) {
      toast.error('El límite diario no puede ser mayor al mensual');
      return;
    }
    
    if (accion === 'RECHAZAR_LIMITES' && !limitesComentario.trim()) {
      toast.error('Debe ingresar un motivo para rechazar los límites');
      return;
    }
    
    setModificandoLimites(true);
    try {
      await api.post(`/onboarding/requests/${selectedRequest.id}/modificar-limites`, {
        limite_diario: diario,
        limite_mensual: mensual,
        accion: accion,
        comentario: limitesComentario
      });
      
      const accionLabel = {
        'MODIFICAR': 'modificados',
        'APROBAR_LIMITES': 'aprobados',
        'RECHAZAR_LIMITES': 'rechazados'
      }[accion];
      
      toast.success(`Límites ${accionLabel} correctamente`);
      setShowModificarLimites(false);
      setNuevosLimites({ diario: '', mensual: '' });
      setLimitesComentario('');
      loadRequestDetail(selectedRequest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al modificar límites');
    } finally {
      setModificandoLimites(false);
    }
  };

  const handleEditRequest = () => {
    setEditData({
      representante_nombre: selectedRequest.representante_nombre,
      representante_email: selectedRequest.representante_email,
      empresa_data: { ...selectedRequest.empresa_data },
      limites: { ...selectedRequest.limites }
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/requests/${selectedRequest.id}`, editData);
      toast.success('Solicitud actualizada');
      setShowEditDialog(false);
      loadRequestDetail(selectedRequest.id);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm('¿Está seguro de eliminar esta solicitud? Esta acción no se puede deshacer.')) return;
    
    try {
      await api.delete(`/onboarding/requests/${selectedRequest.id}`);
      toast.success('Solicitud eliminada');
      setShowDetailDialog(false);
      loadRequests();
      loadCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleDocumentDecision = async () => {
    if (!docDecision) {
      toast.error('Seleccione una decisión');
      return;
    }
    
    try {
      await api.post(`/onboarding/requests/${selectedRequest.id}/documents/${selectedDocument.id}/decide`, {
        decision: docDecision,
        comentario: docDecisionComment
      });
      toast.success(`Documento ${docDecision.toLowerCase()}`);
      setShowDocDecisionDialog(false);
      setSelectedDocument(null);
      setDocDecision('');
      setDocDecisionComment('');
      loadRequestDetail(selectedRequest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar documento');
    }
  };

  const openDocDecision = (doc) => {
    setSelectedDocument(doc);
    setDocDecision('');
    setDocDecisionComment('');
    setShowDocDecisionDialog(true);
  };

  const getEstadoInfo = (estado) => ESTADOS[estado] || ESTADOS.PENDIENTE;

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.empresa_nombre_inicial?.toLowerCase().includes(search) ||
      req.representante_nombre?.toLowerCase().includes(search) ||
      req.representante_email?.toLowerCase().includes(search) ||
      req.empresa_data?.cuit?.includes(search)
    );
  });

  return (
    <div className="p-6 space-y-6" data-testid="solicitudes-onboarding-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="text-blue-600" />
            Solicitudes de Onboarding
          </h1>
          <p className="text-slate-500">Gestione las solicitudes de alta de empresas</p>
        </div>
        <Button variant="outline" onClick={() => { loadRequests(); loadCounts(); }}>
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card className="p-3 cursor-pointer hover:bg-purple-50" onClick={() => setFilterEstado('ENVIADO')}>
          <div className="flex items-center gap-2">
            <Send className="text-purple-600" size={20} />
            <div>
              <p className="text-xl font-bold text-purple-900">{counts.ENVIADO || 0}</p>
              <p className="text-xs text-purple-600">Enviadas</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-amber-50" onClick={() => setFilterEstado('EN_REVISION')}>
          <div className="flex items-center gap-2">
            <Eye className="text-amber-600" size={20} />
            <div>
              <p className="text-xl font-bold text-amber-900">{counts.EN_REVISION || 0}</p>
              <p className="text-xs text-amber-600">En Revisión</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-cyan-50" onClick={() => setFilterEstado('NIVEL_1_APROBADO')}>
          <div className="flex items-center gap-2">
            <CheckCircle className="text-cyan-600" size={20} />
            <div>
              <p className="text-xl font-bold text-cyan-900">{counts.NIVEL_1_APROBADO || 0}</p>
              <p className="text-xs text-cyan-600">Nivel 1</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-amber-50" onClick={() => setFilterEstado('NIVEL_1_DEVUELTO')}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={20} />
            <div>
              <p className="text-xl font-bold text-amber-900">{counts.NIVEL_1_DEVUELTO || 0}</p>
              <p className="text-xs text-amber-600">Devueltas</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-blue-50" onClick={() => setFilterEstado('EN_PROGRESO')}>
          <div className="flex items-center gap-2">
            <RefreshCw className="text-blue-600" size={20} />
            <div>
              <p className="text-xl font-bold text-blue-900">{counts.EN_PROGRESO || 0}</p>
              <p className="text-xs text-blue-600">En Progreso</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-orange-50" onClick={() => setFilterEstado('INFO_ADICIONAL')}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-orange-600" size={20} />
            <div>
              <p className="text-xl font-bold text-orange-900">{counts.INFO_ADICIONAL || 0}</p>
              <p className="text-xs text-orange-600">Info Adicional</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-green-50" onClick={() => setFilterEstado('APROBADO')}>
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            <div>
              <p className="text-xl font-bold text-green-900">{counts.APROBADO || 0}</p>
              <p className="text-xs text-green-600">Aprobadas</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 cursor-pointer hover:bg-slate-100" onClick={() => setFilterEstado('')}>
          <div className="flex items-center gap-2">
            <Building2 className="text-slate-600" size={20} />
            <div>
              <p className="text-xl font-bold text-slate-900">
                {Object.values(counts).reduce((a, b) => typeof b === 'number' ? a + b : a, 0) - (counts.total_pendientes || 0)}
              </p>
              <p className="text-xs text-slate-600">Total</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por empresa, representante o CUIT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={filterEstado || "TODOS"} onValueChange={(val) => setFilterEstado(val === "TODOS" ? "" : val)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos los estados</SelectItem>
              {Object.entries(ESTADOS).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empresa</TableHead>
              <TableHead>Representante</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>CUIT Rep.</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <RefreshCw className="animate-spin mx-auto text-blue-600" size={24} />
                </TableCell>
              </TableRow>
            ) : filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  No hay solicitudes
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map(req => {
                const estadoInfo = getEstadoInfo(req.estado);
                const EstadoIcon = estadoInfo.icon;
                
                return (
                  <TableRow key={req.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">
                            {req.empresa_data?.razon_social || req.empresa_nombre_inicial}
                          </p>
                          {req.tipo_empresa === 'COOPERATIVA' && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5">
                              Coop
                            </Badge>
                          )}
                        </div>
                        {req.empresa_data?.cuit && (
                          <p className="text-xs text-slate-500">{req.empresa_data.cuit}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm text-slate-900">{req.representante_nombre}</p>
                        <p className="text-xs text-slate-500">{req.representante_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={estadoInfo.color}>
                        <EstadoIcon size={12} className="mr-1" />
                        {estadoInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs font-mono text-slate-600">
                        {req.representante_cuit || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-slate-500">{formatDate(req.created_at)}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadRequestDetail(req.id)}
                          title="Vista rápida"
                        >
                          <Eye size={16} />
                        </Button>
                        {!['EN_REVISION', 'APROBADO', 'RECHAZADO'].includes(req.estado) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            data-testid={`enviar-revision-list-${req.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const nombre = req.empresa_data?.razon_social || req.empresa_nombre_inicial || '';
                              if (!window.confirm(`¿Enviar a revisión la solicitud de "${nombre}"?\n\nSe notificará al cliente por email.`)) return;
                              try {
                                await api.post(`/onboarding/requests/${req.id}/enviar-a-revision`);
                                toast.success('Solicitud enviada a revisión');
                                loadRequests();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Error');
                              }
                            }}
                            title="Enviar a revisión"
                          >
                            <Send size={14} className="mr-1" />
                            Revisión
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/m/empresas/solicitudes-onboarding/${req.id}`)}
                          data-testid={`review-request-${req.id}`}
                        >
                          Revisar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {loadingDetail ? (
            <div className="py-12 text-center">
              <RefreshCw className="animate-spin mx-auto text-blue-600" size={32} />
            </div>
          ) : selectedRequest && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="text-blue-600" />
                    {selectedRequest.empresa_data?.razon_social || selectedRequest.empresa_nombre_inicial}
                    {selectedRequest.tipo_empresa === 'COOPERATIVA' && (
                      <Badge className="bg-purple-100 text-purple-700">Cooperativa</Badge>
                    )}
                  </span>
                  <Badge className={getEstadoInfo(selectedRequest.estado).color}>
                    {getEstadoInfo(selectedRequest.estado).label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="datos" className="mt-4">
                <TabsList>
                  <TabsTrigger value="datos">Datos</TabsTrigger>
                  <TabsTrigger value="documentos">Documentos</TabsTrigger>
                  <TabsTrigger value="historial">Historial</TabsTrigger>
                  <TabsTrigger value="comentarios">Comentarios</TabsTrigger>
                </TabsList>

                {/* Tab Datos */}
                <TabsContent value="datos" className="space-y-4 mt-4">
                  {/* Tipo de Empresa */}
                  {selectedRequest.tipo_empresa === 'COOPERATIVA' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-sm text-purple-700 flex items-center gap-2">
                        <Users size={16} />
                        <span className="font-medium">Cooperativa</span> - Documentación específica requerida
                      </p>
                    </div>
                  )}
                  
                  {/* Representante Legal */}
                  <Card className="p-4 bg-blue-50">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <User size={16} className="text-blue-600" />
                      Representante Legal
                      {selectedRequest.padron_cliente_id && (
                        <Badge className="bg-green-100 text-green-700 text-xs">Vinculado al Padrón</Badge>
                      )}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-slate-500">Nombre</Label>
                        <p className="font-medium">
                          {selectedRequest.representante_padron?.nombre 
                            ? `${selectedRequest.representante_padron.nombre} ${selectedRequest.representante_padron.apellido || ''}`
                            : selectedRequest.representante_nombre}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">CUIT/DNI</Label>
                        <p className="font-mono">
                          {selectedRequest.representante_padron?.cuit || 
                           selectedRequest.representante_padron?.dni || 
                           selectedRequest.representante_cuit || '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Email</Label>
                        <p>{selectedRequest.representante_padron?.mail || selectedRequest.representante_email}</p>
                      </div>
                      {(selectedRequest.representante_padron?.telefono) && (
                        <div>
                          <Label className="text-xs text-slate-500">Teléfono</Label>
                          <p>{selectedRequest.representante_padron.telefono}</p>
                        </div>
                      )}
                      {(selectedRequest.representante_padron?.domicilio) && (
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Domicilio</Label>
                          <p>
                            {selectedRequest.representante_padron.domicilio}
                            {selectedRequest.representante_padron.localidad && `, ${selectedRequest.representante_padron.localidad}`}
                            {selectedRequest.representante_padron.provincia && ` - ${selectedRequest.representante_padron.provincia}`}
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedRequest.padron_cliente_id && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/m/clientes/padron/${selectedRequest.padron_cliente_id}`)}
                          className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        >
                          <Eye size={14} className="mr-2" />
                          Ver Movimientos del Representante
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Empresa */}
                  <Card className="p-4">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 size={16} className="text-purple-600" />
                      Datos de la Empresa
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-slate-500">Razón Social</Label>
                        <p>{selectedRequest.empresa_data?.razon_social || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">CUIT</Label>
                        <p className="font-mono">{selectedRequest.empresa_data?.cuit || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Domicilio</Label>
                        <p>
                          {selectedRequest.empresa_data?.domicilio}, {selectedRequest.empresa_data?.localidad}, {selectedRequest.empresa_data?.provincia}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Teléfono</Label>
                        <p>{selectedRequest.empresa_data?.telefono || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Email</Label>
                        <p>{selectedRequest.empresa_data?.email || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Actividad Principal</Label>
                        <p>{selectedRequest.empresa_data?.actividad_principal || '-'}</p>
                      </div>
                    </div>
                  </Card>

                </TabsContent>

                {/* Tab Documentos */}
                <TabsContent value="documentos" className="mt-4">
                  <div className="space-y-3">
                    {selectedRequest.documentos_requeridos?.map(doc => {
                      const uploaded = selectedRequest.documentos?.find(d => d.tipo_documento_id === doc.id);
                      
                      return (
                        <div 
                          key={doc.id}
                          className={`p-3 border rounded-lg ${
                            uploaded?.estado === 'APROBADO' ? 'border-green-300 bg-green-50' :
                            uploaded?.estado === 'RECHAZADO' ? 'border-red-300 bg-red-50' :
                            doc.subido ? 'border-blue-200 bg-blue-50' : 'border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {uploaded?.estado === 'APROBADO' ? (
                                <CheckCircle className="text-green-600" size={20} />
                              ) : uploaded?.estado === 'RECHAZADO' ? (
                                <XCircle className="text-red-600" size={20} />
                              ) : doc.subido ? (
                                <FileText className="text-blue-600" size={20} />
                              ) : (
                                <FileText className="text-slate-400" size={20} />
                              )}
                              <div>
                                <p className="font-medium">{doc.nombre}</p>
                                {uploaded && (
                                  <>
                                    <p className="text-xs text-slate-500">
                                      {uploaded.filename} • {(uploaded.size / 1024).toFixed(1)} KB
                                    </p>
                                    {uploaded.decision_comentario && (
                                      <p className="text-xs text-slate-600 mt-1">
                                        <strong>Comentario:</strong> {uploaded.decision_comentario}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {uploaded?.estado === 'APROBADO' && (
                                <Badge className="bg-green-100 text-green-700">Aprobado</Badge>
                              )}
                              {uploaded?.estado === 'RECHAZADO' && (
                                <Badge className="bg-red-100 text-red-700">Rechazado</Badge>
                              )}
                              {!doc.subido && (
                                <Badge variant="outline" className="text-amber-600">Pendiente</Badge>
                              )}
                              {uploaded && !uploaded.estado && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDocDecision(uploaded)}
                                  className="text-xs"
                                >
                                  Revisar
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                {/* Tab Historial */}
                <TabsContent value="historial" className="mt-4">
                  <div className="space-y-3">
                    {selectedRequest.historial_estados?.map((h, idx) => (
                      <div key={idx} className="flex gap-3 p-3 border rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getEstadoInfo(h.estado).color}`}>
                          {React.createElement(getEstadoInfo(h.estado).icon, { size: 16 })}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{getEstadoInfo(h.estado).label}</p>
                          <p className="text-xs text-slate-500">{formatDate(h.fecha)} • {h.usuario}</p>
                          {h.comentario && <p className="text-sm text-slate-600 mt-1">{h.comentario}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab Comentarios */}
                <TabsContent value="comentarios" className="mt-4">
                  <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                    {selectedRequest.comentarios?.length === 0 ? (
                      <p className="text-center text-slate-400 py-8">Sin comentarios</p>
                    ) : (
                      selectedRequest.comentarios?.map(c => (
                        <div 
                          key={c.id} 
                          className={`p-3 rounded-lg ${
                            c.is_internal ? 'bg-amber-50 border border-amber-200' :
                            c.user_id === 'client' ? 'bg-blue-50' : 'bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium">{c.user_name}</span>
                            {c.is_internal && <Badge className="text-xs bg-amber-100 text-amber-700">Interno</Badge>}
                            <span className="text-xs text-slate-400">{formatDate(c.created_at)}</span>
                          </div>
                          <p className="text-sm">{c.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex gap-2 mb-2">
                      <Input
                        placeholder="Escribir comentario..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                      />
                      <Button onClick={handleAddComment}>Enviar</Button>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isInternalComment}
                        onChange={(e) => setIsInternalComment(e.target.checked)}
                      />
                      <span className="text-slate-600">Comentario interno (no visible para el cliente)</span>
                    </label>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Actions - Mostrar para estados que requieren acción */}
              {['ENVIADO', 'EN_REVISION', 'PENDIENTE', 'EN_PROGRESO'].includes(selectedRequest.estado) && (
                <div className="flex justify-between items-center pt-4 border-t mt-4">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleResendCredentials(selectedRequest.id)}
                    >
                      <Key size={16} className="mr-2" />
                      Reenviar Credenciales
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="extend-password-btn"
                      onClick={() => {
                        setExtendNoExpiration(!selectedRequest.password_expires_at);
                        setExtendHours(168);
                        setShowExtendDialog(true);
                      }}
                    >
                      <Clock size={16} className="mr-2" />
                      Extender Vencimiento
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="ver-credenciales-btn"
                      onClick={openCredsDialog}
                    >
                      <Link2 size={16} className="mr-2" />
                      Ver / Cambiar Acceso
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleEditRequest}
                    >
                      <Edit2 size={16} className="mr-2" />
                      Modificar
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    {/* Solo mostrar botones de decisión si el estado es ENVIADO o EN_REVISION */}
                    {['ENVIADO', 'EN_REVISION'].includes(selectedRequest.estado) ? (
                      <>
                        <Button
                          variant="outline"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={() => {
                            setDecision('INFO_ADICIONAL');
                            setDecisionComment('');
                            setShowDecisionDialog(true);
                          }}
                        >
                          <AlertTriangle size={16} className="mr-2" />
                          Pedir Info
                        </Button>
                        <Button
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => {
                            setDecision('RECHAZADO');
                            setDecisionComment('');
                            setShowDecisionDialog(true);
                          }}
                        >
                          <XCircle size={16} className="mr-2" />
                          Rechazar
                        </Button>
                        <Button
                          className="bg-cyan-600 hover:bg-cyan-700 text-white"
                          onClick={() => {
                            setDecision('NIVEL_1_APROBADO');
                            setDecisionComment('');
                            setLimiteDiario('');
                            setLimiteMensual('');
                            setShowDecisionDialog(true);
                          }}
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Aprobar (Nivel 1)
                        </Button>
                      </>
                    ) : (
                      /* Para PENDIENTE o EN_PROGRESO, mostrar botón para forzar revisión */
                      <Button
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={async () => {
                          try {
                            await api.post(`/onboarding/requests/${selectedRequest.id}/forzar-revision`);
                            toast.success('Solicitud marcada para revisión');
                            loadRequestDetail(selectedRequest.id);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Error');
                          }
                        }}
                      >
                        <Send size={16} className="mr-2" />
                        Forzar Revisión
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Actions for NIVEL_1_APROBADO - Second level review */}
              {selectedRequest.estado === 'NIVEL_1_APROBADO' && (
                <div className="space-y-4 pt-4 border-t mt-4">
                  {/* Info del nivel 1 */}
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <h4 className="font-medium text-cyan-800 mb-2 flex items-center gap-2">
                      <CheckCircle size={18} />
                      Aprobación Nivel 1
                    </h4>
                    <div className="text-sm space-y-1 text-cyan-700">
                      <p><span className="font-medium">Aprobado por:</span> {selectedRequest.nivel_1?.aprobado_por_email}</p>
                      <p><span className="font-medium">Fecha:</span> {formatDate(selectedRequest.nivel_1?.aprobado_at)}</p>
                      <p><span className="font-medium">Límite Diario Propuesto:</span> ${selectedRequest.nivel_1?.limites_propuestos?.diario?.toLocaleString('es-AR')}</p>
                      <p><span className="font-medium">Límite Mensual Propuesto:</span> ${selectedRequest.nivel_1?.limites_propuestos?.mensual?.toLocaleString('es-AR')}</p>
                      {selectedRequest.nivel_1?.comentario && (
                        <p><span className="font-medium">Comentario:</span> {selectedRequest.nivel_1?.comentario}</p>
                      )}
                      {selectedRequest.nivel_1?.limites_modificados_por && (
                        <p className="text-amber-600"><span className="font-medium">Límites modificados por:</span> {selectedRequest.nivel_1?.limites_modificados_por}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Sección para modificar límites */}
                  {!showModificarLimites ? (
                    <Button
                      variant="outline"
                      className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      onClick={() => {
                        setNuevosLimites({
                          diario: selectedRequest.nivel_1?.limites_propuestos?.diario?.toString() || '',
                          mensual: selectedRequest.nivel_1?.limites_propuestos?.mensual?.toString() || ''
                        });
                        setLimitesComentario('');
                        setShowModificarLimites(true);
                      }}
                    >
                      <Edit2 size={16} className="mr-2" />
                      Modificar Límites Propuestos
                    </Button>
                  ) : (
                    <div className="bg-slate-50 border rounded-lg p-4 space-y-3">
                      <h4 className="font-medium text-slate-800 flex items-center gap-2">
                        <DollarSign size={16} />
                        Modificar Límites
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Límite Diario *</Label>
                          <Input
                            type="number"
                            placeholder="Ej: 500000"
                            value={nuevosLimites.diario}
                            onChange={(e) => setNuevosLimites({...nuevosLimites, diario: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Límite Mensual *</Label>
                          <Input
                            type="number"
                            placeholder="Ej: 5000000"
                            value={nuevosLimites.mensual}
                            onChange={(e) => setNuevosLimites({...nuevosLimites, mensual: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Comentario (obligatorio para rechazar)</Label>
                        <Textarea
                          placeholder="Motivo de la modificación..."
                          value={limitesComentario}
                          onChange={(e) => setLimitesComentario(e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setShowModificarLimites(false)}>
                          Cancelar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          disabled={modificandoLimites}
                          onClick={() => handleModificarLimites('RECHAZAR_LIMITES')}
                        >
                          <XCircle size={14} className="mr-1" />
                          Rechazar Límites
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          disabled={modificandoLimites}
                          onClick={() => handleModificarLimites('MODIFICAR')}
                        >
                          <Edit2 size={14} className="mr-1" />
                          Solo Modificar
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={modificandoLimites}
                          onClick={() => handleModificarLimites('APROBAR_LIMITES')}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Aprobar Límites
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      onClick={() => {
                        setDecision('NIVEL_1_DEVUELTO');
                        setDecisionComment('');
                        setShowDecisionDialog(true);
                      }}
                    >
                      <AlertTriangle size={16} className="mr-2" />
                      Devolver a Nivel 1
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => {
                        setDecision('APROBADO');
                        setDecisionComment('');
                        // Pre-cargar límites propuestos
                        setLimiteDiario(selectedRequest.nivel_1?.limites_propuestos?.diario?.toString() || '');
                        setLimiteMensual(selectedRequest.nivel_1?.limites_propuestos?.mensual?.toString() || '');
                        setShowDecisionDialog(true);
                      }}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Aprobar Definitivo
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Actions for NIVEL_1_DEVUELTO - First level can fix or close */}
              {selectedRequest.estado === 'NIVEL_1_DEVUELTO' && (
                <div className="space-y-4 pt-4 border-t mt-4">
                  {/* Info de la devolución */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle size={18} />
                      Solicitud Devuelta por Nivel 2
                    </h4>
                    <div className="text-sm space-y-1 text-amber-700">
                      <p><span className="font-medium">Devuelto por:</span> {selectedRequest.nivel_2_rechazo?.rechazado_por_email}</p>
                      <p><span className="font-medium">Fecha:</span> {formatDate(selectedRequest.nivel_2_rechazo?.rechazado_at)}</p>
                      <p><span className="font-medium">Motivo:</span> {selectedRequest.nivel_2_rechazo?.motivo}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => {
                        setDecision('RECHAZADO');
                        setDecisionComment('');
                        setShowDecisionDialog(true);
                      }}
                    >
                      <XCircle size={16} className="mr-2" />
                      Cerrar como Rechazada
                    </Button>
                    <Button
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      onClick={() => {
                        setDecision('NIVEL_1_APROBADO');
                        setDecisionComment('');
                        setLimiteDiario(selectedRequest.nivel_1?.limites_propuestos?.diario?.toString() || '');
                        setLimiteMensual(selectedRequest.nivel_1?.limites_propuestos?.mensual?.toString() || '');
                        setShowDecisionDialog(true);
                      }}
                    >
                      <Send size={16} className="mr-2" />
                      Corregir y Reenviar
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Info for INFO_ADICIONAL state */}
              {selectedRequest.estado === 'INFO_ADICIONAL' && (
                <div className="space-y-4 pt-4 border-t mt-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                      <AlertTriangle size={18} />
                      Pendiente de Información Adicional
                    </h4>
                    <p className="text-sm text-orange-700">
                      Se ha solicitado información adicional al cliente. Espere a que el cliente complete los datos faltantes y reenvíe la solicitud.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleResendCredentials(selectedRequest.id)}
                    >
                      <Key size={16} className="mr-2" />
                      Reenviar Credenciales
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Actions for other states (PENDIENTE, EN_PROGRESO, RECHAZADO) */}
              {!['ENVIADO', 'EN_REVISION', 'APROBADO', 'NIVEL_1_APROBADO', 'NIVEL_1_DEVUELTO', 'INFO_ADICIONAL'].includes(selectedRequest.estado) && (
                <div className="space-y-4 pt-4 border-t mt-4">
                  {/* Para PENDIENTE o EN_PROGRESO, mostrar opciones de aprobación directa */}
                  {['PENDIENTE', 'EN_PROGRESO'].includes(selectedRequest.estado) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800 mb-3">
                        <AlertTriangle size={16} className="inline mr-2" />
                        Esta solicitud está en estado <strong>{selectedRequest.estado}</strong>. 
                        El cliente aún no ha enviado la documentación completa, pero puede aprobarla si lo considera necesario.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          className="border-purple-500 text-purple-600 hover:bg-purple-50"
                          onClick={async () => {
                            try {
                              await api.post(`/onboarding/requests/${selectedRequest.id}/forzar-revision`);
                              toast.success('Solicitud marcada como enviada');
                              loadRequestDetail(selectedRequest.id);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error');
                            }
                          }}
                        >
                          <Send size={16} className="mr-2" />
                          Marcar como Enviado
                        </Button>
                        <Button
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="enviar-revision-btn"
                          onClick={async () => {
                            if (!window.confirm(`¿Enviar a revisión la solicitud de "${selectedRequest.empresa_nombre_inicial || selectedRequest.empresa_data?.razon_social || ''}"?\n\nSe notificará al cliente por email.`)) return;
                            try {
                              await api.post(`/onboarding/requests/${selectedRequest.id}/enviar-a-revision`);
                              toast.success('Solicitud enviada a revisión. Se notificó al cliente.');
                              loadRequestDetail(selectedRequest.id);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error al enviar a revisión');
                            }
                          }}
                        >
                          <Send size={16} className="mr-2" />
                          Enviar a Revisión
                        </Button>
                        <Button
                          className="bg-cyan-600 hover:bg-cyan-700 text-white"
                          onClick={() => {
                            setDecision('NIVEL_1_APROBADO');
                            setDecisionComment('');
                            setLimiteDiario('');
                            setLimiteMensual('');
                            setShowDecisionDialog(true);
                          }}
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Aprobar Directamente (Nivel 1)
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-slate-500">
                      Estado: <span className="font-medium">{ESTADOS[selectedRequest.estado]?.label || selectedRequest.estado}</span>
                      {selectedRequest.estado === 'RECHAZADO' && ' - Esta solicitud ya fue rechazada'}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleEditRequest}
                      >
                        <Edit2 size={16} className="mr-2" />
                        Modificar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDeleteRequest}
                        style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                        className="hover:bg-red-50"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Eliminar Solicitud
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {decision === 'NIVEL_1_APROBADO' && 'Aprobar Solicitud (Nivel 1)'}
              {decision === 'APROBADO' && 'Aprobar Definitivo (Nivel 2)'}
              {decision === 'RECHAZADO' && 'Rechazar Solicitud'}
              {decision === 'INFO_ADICIONAL' && 'Solicitar Información Adicional'}
              {decision === 'NIVEL_1_DEVUELTO' && 'Devolver a Nivel 1'}
            </DialogTitle>
            <DialogDescription>
              {decision === 'NIVEL_1_APROBADO' && 'Proponga los límites. La solicitud pasará a un segundo nivel de aprobación.'}
              {decision === 'APROBADO' && 'Confirme o modifique los límites. La empresa será creada automáticamente.'}
              {decision === 'RECHAZADO' && 'Indique el motivo del rechazo. El cliente será notificado.'}
              {decision === 'INFO_ADICIONAL' && 'Indique qué información necesita. El cliente podrá modificar su solicitud.'}
              {decision === 'NIVEL_1_DEVUELTO' && 'Indique el motivo de la devolución. El primer revisor podrá corregir o cerrar la solicitud.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Info de límites propuestos para nivel 2 */}
            {decision === 'APROBADO' && selectedRequest?.nivel_1?.limites_propuestos && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800 mb-1">Límites propuestos por Nivel 1:</p>
                <p className="text-blue-700">
                  Diario: ${selectedRequest.nivel_1.limites_propuestos.diario?.toLocaleString('es-AR')} | 
                  Mensual: ${selectedRequest.nivel_1.limites_propuestos.mensual?.toLocaleString('es-AR')}
                </p>
                <p className="text-blue-600 text-xs mt-1">Puede mantenerlos o modificarlos abajo</p>
              </div>
            )}
            
            {/* Campos de límites para aprobaciones */}
            {(decision === 'NIVEL_1_APROBADO' || decision === 'APROBADO') && (
              <div className={`space-y-4 p-4 rounded-lg border ${
                decision === 'NIVEL_1_APROBADO' ? 'bg-cyan-50 border-cyan-200' : 'bg-green-50 border-green-200'
              }`}>
                <h4 className={`font-medium flex items-center gap-2 ${
                  decision === 'NIVEL_1_APROBADO' ? 'text-cyan-800' : 'text-green-800'
                }`}>
                  <DollarSign size={18} />
                  {decision === 'NIVEL_1_APROBADO' ? 'Límites Propuestos' : 'Límites Finales'}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Límite Diario ($)</Label>
                    <Input
                      type="number"
                      placeholder="Ej: 500000"
                      value={limiteDiario}
                      onChange={(e) => setLimiteDiario(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Límite Mensual ($)</Label>
                    <Input
                      type="number"
                      placeholder="Ej: 10000000"
                      value={limiteMensual}
                      onChange={(e) => setLimiteMensual(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label>
                Comentario / Motivo 
                {['RECHAZADO', 'INFO_ADICIONAL', 'NIVEL_1_DEVUELTO'].includes(decision) && (
                  <span className="text-red-500">*</span>
                )}
              </Label>
              <Textarea
                rows={3}
                placeholder={
                  decision === 'NIVEL_1_APROBADO' ? 'Comentario para el segundo revisor (opcional)...' :
                  decision === 'APROBADO' ? 'Comentario opcional...' :
                  decision === 'RECHAZADO' ? 'Indique el motivo del rechazo...' :
                  decision === 'NIVEL_1_DEVUELTO' ? 'Indique qué debe corregir el primer revisor...' :
                  'Indique qué información adicional necesita...'
                }
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDecision}
              disabled={deciding || (
                (decision === 'NIVEL_1_APROBADO' && (!limiteDiario || !limiteMensual)) ||
                (decision === 'APROBADO' && (!limiteDiario || !limiteMensual)) ||
                (['RECHAZADO', 'INFO_ADICIONAL', 'NIVEL_1_DEVUELTO'].includes(decision) && !decisionComment.trim())
              )}
              className={`text-white ${
                decision === 'NIVEL_1_APROBADO' ? 'bg-cyan-600 hover:bg-cyan-700' :
                decision === 'APROBADO' ? 'bg-green-600 hover:bg-green-700' :
                decision === 'RECHAZADO' ? 'bg-red-600 hover:bg-red-700' :
                decision === 'NIVEL_1_DEVUELTO' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {deciding ? 'Procesando...' : (
                decision === 'NIVEL_1_APROBADO' ? 'Aprobar y Enviar a Nivel 2' :
                decision === 'APROBADO' ? 'Aprobar Definitivamente' :
                decision === 'NIVEL_1_DEVUELTO' ? 'Devolver' :
                'Confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Password Dialog */}
      {/* Dialog: Ver / Cambiar credenciales de acceso */}
      <Dialog open={showCredsDialog} onOpenChange={(o) => { if (!credsSetting) setShowCredsDialog(o); }}>
        <DialogContent style={{ backgroundColor: '#ffffff' }} className="max-w-lg" data-testid="credenciales-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 size={18} /> Acceso del cliente al portal
            </DialogTitle>
            <DialogDescription>
              Probá el acceso del cliente: copiá el link, seteá una contraseña y decidí si la reenviás por email.
            </DialogDescription>
          </DialogHeader>

          {credsLoading ? (
            <div className="py-8 text-center text-slate-500" data-testid="creds-loading">
              <RefreshCw size={22} className="animate-spin mx-auto mb-2" /> Cargando...
            </div>
          ) : credsInfo ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Link de acceso</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={credsInfo.portal_link || ''} className="text-xs font-mono" data-testid="creds-portal-link"
                    style={{ backgroundColor: '#f8fafc' }} />
                  <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar link"
                    onClick={() => copyToClipboard(credsInfo.portal_link, 'Link')} data-testid="creds-copy-link-btn">
                    <Copy size={14} />
                  </Button>
                  {credsInfo.portal_link && (
                    <a href={credsInfo.portal_link} target="_blank" rel="noreferrer">
                      <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Abrir en nueva pestaña" data-testid="creds-open-link-btn">
                        <ExternalLink size={14} />
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Email de acceso</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={credsInfo.email || ''} className="text-sm font-mono" data-testid="creds-email"
                    style={{ backgroundColor: '#f8fafc' }} />
                  <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar email"
                    onClick={() => copyToClipboard(credsInfo.email, 'Email')} data-testid="creds-copy-email-btn">
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-xs mt-1.5" data-testid="creds-expiry-status">
                  {credsInfo.has_expiration
                    ? (credsInfo.is_expired
                        ? <span className="text-red-600 font-medium">Clave actual VENCIDA ({new Date(credsInfo.password_expires_at).toLocaleString('es-AR')})</span>
                        : <span className="text-slate-500">Clave vence: {new Date(credsInfo.password_expires_at).toLocaleString('es-AR')}</span>)
                    : <span className="text-slate-500">Clave temporal sin vencimiento</span>}
                </p>
              </div>

              {credsResult ? (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="creds-result">
                  <p className="text-xs text-emerald-700 uppercase font-medium mb-1">Nueva contraseña temporal</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={credsResult.temp_password} className="text-sm font-mono font-semibold" data-testid="creds-new-password"
                      style={{ backgroundColor: '#ffffff' }} />
                    <Button type="button" variant="outline" size="sm" className="h-9 px-2" title="Copiar contraseña"
                      onClick={() => copyToClipboard(credsResult.temp_password, 'Contraseña')} data-testid="creds-copy-password-btn">
                      <Copy size={14} />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {credsResult.email_sent
                      ? 'Reenviada por email al cliente.'
                      : 'No se envió por email. Copiala para probar el login manualmente.'}
                  </p>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Setear contraseña (opcional)</Label>
                    <Input
                      value={credsCustomPassword}
                      onChange={(e) => setCredsCustomPassword(e.target.value)}
                      placeholder="Dejar vacío para generar una aleatoria"
                      className="text-sm mt-1"
                      data-testid="creds-custom-password-input"
                      style={{ backgroundColor: '#ffffff' }}
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Útil para reproducir un error de login con una clave conocida.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={credsSendEmail} onChange={(e) => setCredsSendEmail(e.target.checked)} data-testid="creds-send-email-checkbox" />
                    Reenviar credenciales por email al cliente
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-slate-500">No se pudieron cargar las credenciales.</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredsDialog(false)} disabled={credsSetting} data-testid="creds-close-btn">
              Cerrar
            </Button>
            {credsInfo && !credsResult && (
              <Button onClick={handleSetPassword} disabled={credsSetting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="creds-set-password-btn">
                {credsSetting ? <><RefreshCw size={14} className="animate-spin mr-1" /> Aplicando...</> : (credsCustomPassword.trim() ? 'Setear contraseña' : 'Generar contraseña')}
              </Button>
            )}
            {credsResult && (
              <Button onClick={() => { setCredsResult(null); setCredsCustomPassword(''); }} variant="outline" data-testid="creds-another-btn">
                Setear otra
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }} data-testid="extend-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={18} /> Vencimiento de la clave temporal
            </DialogTitle>
            <DialogDescription>
              Ajustá la duración de la clave sin regenerar la contraseña actual.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Estado actual:{' '}
              <strong>
                {selectedRequest?.password_expires_at
                  ? `vence ${new Date(selectedRequest.password_expires_at).toLocaleString('es-AR')}`
                  : 'sin vencimiento'}
              </strong>
            </p>
            <div>
              <Label>Nueva duración (desde ahora)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  className="w-28"
                  data-testid="extend-hours-input"
                  disabled={extendNoExpiration}
                  value={extendNoExpiration ? '' : extendHours}
                  onChange={(e) => setExtendHours(parseInt(e.target.value) || 0)}
                />
                <span className="text-slate-500">horas</span>
                <div className="flex gap-1 ml-2">
                  {[{ l: '24h', v: 24 }, { l: '7 días', v: 168 }, { l: '30 días', v: 720 }].map((p) => (
                    <Button key={p.v} type="button" variant="outline" size="sm" className="h-7 text-xs"
                      disabled={extendNoExpiration}
                      onClick={() => setExtendHours(p.v)} data-testid={`extend-preset-${p.v}`}>
                      {p.l}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="extend-no-expiration-checkbox"
                checked={extendNoExpiration}
                onChange={(e) => setExtendNoExpiration(e.target.checked)}
              />
              Sin vencimiento (la clave nunca expira)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)} disabled={extending} data-testid="extend-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleExtendPassword} disabled={extending} data-testid="extend-confirm-btn">
              {extending ? 'Guardando...' : 'Aplicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" size={20} />
              Modificar Solicitud
            </DialogTitle>
            <DialogDescription>
              Edite los datos de la solicitud de onboarding
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Representante */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Representante</Label>
                <Input
                  value={editData.representante_nombre || ''}
                  onChange={(e) => setEditData({ ...editData, representante_nombre: e.target.value })}
                />
              </div>
              <div>
                <Label>Email Representante</Label>
                <Input
                  type="email"
                  value={editData.representante_email || ''}
                  onChange={(e) => setEditData({ ...editData, representante_email: e.target.value })}
                />
              </div>
            </div>
            
            {/* Empresa */}
            <Card className="p-4 bg-slate-50">
              <h4 className="font-semibold text-slate-700 mb-3">Datos de la Empresa</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Razón Social</Label>
                  <Input
                    value={editData.empresa_data?.razon_social || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, razon_social: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>CUIT</Label>
                  <Input
                    value={editData.empresa_data?.cuit || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, cuit: e.target.value }
                    })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Domicilio</Label>
                  <Input
                    value={editData.empresa_data?.domicilio || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, domicilio: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>Localidad</Label>
                  <Input
                    value={editData.empresa_data?.localidad || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, localidad: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input
                    value={editData.empresa_data?.provincia || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      empresa_data: { ...editData.empresa_data, provincia: e.target.value }
                    })}
                  />
                </div>
              </div>
            </Card>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Decision Dialog */}
      <Dialog open={showDocDecisionDialog} onOpenChange={setShowDocDecisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-600" size={20} />
              Revisar Documento
            </DialogTitle>
            <DialogDescription>
              {selectedDocument && `Documento: ${selectedDocument.filename}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Decisión</Label>
              <Select value={docDecision} onValueChange={setDocDecision}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione una decisión" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APROBADO">Aprobar documento</SelectItem>
                  <SelectItem value="RECHAZADO">Rechazar documento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Comentario (opcional)</Label>
              <Textarea
                rows={3}
                placeholder="Indique observaciones si las hay..."
                value={docDecisionComment}
                onChange={(e) => setDocDecisionComment(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocDecisionDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDocumentDecision}
              disabled={!docDecision}
              className={`text-white ${docDecision === 'APROBADO' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
